import {getBytes, getDownloadURL, ref} from 'firebase/storage'
import {get, httpDelete, patch, post} from '@/services/infrastructure/api.service'
import {endsWith, toLower} from 'lodash'
import {getSelectedOrgId} from '@/tenants/org.service'
import {useStorageService} from '@/services/infrastructure/storage.service'
import {App as VueApp, ref as vueRef} from 'vue'
import {DocumentMeta} from '@/types/transaction/DocumentMeta'
import {downloadFileKey, uploadFilesKey} from '@/types/transaction/Attachment.injects'
import {Tenant} from '@/tenants/Tenant'
import {IncomingDocument} from '@/types/IncomingDocument'
import {DocumentStatusCommand} from '@/document/DocumentStatusCommand.type'
import {APPROVED} from '@/task-manager/IncomingTaskStatus'

const DOCUMENTS = '/documents'
const INCOMING_PATH = DOCUMENTS + '/incoming'
const V2_PATH = '/v2'

const INCOMING_DOCUMENT_CREATED = 'incomingDocumentCreated'
const INCOMING_DOCUMENT_DELETED = 'incomingDocumentDeleted'

const INCOMING_DOCUMENT_EVENTS = [INCOMING_DOCUMENT_CREATED, INCOMING_DOCUMENT_DELETED]

type Uuid = string
const transactionManagerDocumentUrls = vueRef<Map<Uuid, string>>(new Map())

const isFileType = (fileName: string, extensions: string[]) => {
    for (const extension of extensions) {
        if (endsWith(
            toLower(fileName), extension)
        )
            return true
    }
    return false
}

const resolveFileIcon = (fileName: string) => {
    if (isFileType(fileName, ['.pdf'])) {
        return 'i-ph-file-pdf'
    } else if (isFileType(fileName, ['.xls', '.xlsx'])) {
        return 'i-ph-file-xls'
    } else if (isFileType(fileName, ['.doc', '.docx'])) {
        return 'i-ph-file-doc'
    } else if (isFileType(fileName, ['.jpg', '.jpeg', '.png', '.bmp', '.tiff', '.gif'])) {
        return 'i-ph-file-image'
    } else if (isFileType(fileName, ['.xml'])) {
        return 'i-ph-file-code'
    } else if (isFileType(fileName, ['.svg'])) {
        return 'i-ph-file-svg'
    }
    return 'i-ph-file-text'
}

const transactionManagerDocumentUrlsRevoke = (documents: DocumentMeta[]) => {
    const localBlobUrls: string[] = []
    documents.forEach(doc => {
        const url = transactionManagerDocumentUrls.value.get(doc.uuid)
        if (url !== undefined) localBlobUrls.push(url)

        transactionManagerDocumentUrls.value.delete(doc.uuid)
    })

    localBlobUrls.forEach(url => URL.revokeObjectURL(url))
}

const fileUploadBatch = async (files: FileList | File[]): Promise<DocumentMeta[]> => {
    const documentMetas: DocumentMeta[] = []
    for (const file of Array.from(files)) {
        const docMeta = await fileUpload(file)
        documentMetas.push(docMeta)
    }
    return documentMetas
}

const fileUpload = async (file: File): Promise<DocumentMeta> => {
    const form = new FormData()
    form.append('file', file)
    return await post(DOCUMENTS + '/upload-file', form, {tenantId: getSelectedOrgId()})
}


const registerIncomingDocuments = async (documentMetas: DocumentMeta[]) => {
    if (documentMetas.length === 0) return
    // TODO brittle way of checking for existing documents - and functionality specific to create transaction...
    const existingDocs = await listIncomingDocumentsV2()

    const existingDocumentUUIDs = new Set(
        existingDocs.map(doc => doc.documentMeta.uuid)
    )

    const newDocumentMetas = documentMetas.filter(
        doc => !existingDocumentUUIDs.has(doc.uuid)
    )
    await Promise.all(newDocumentMetas.map(registerIncomingDocument))
}

// register: Create invoice draft + incoming document entity
const registerIncomingDocument = async (documentMeta: DocumentMeta): Promise<DocumentMeta | undefined> => {
    return await post(INCOMING_PATH, documentMeta, {tenantId: getSelectedOrgId()})
}

const storageResolveUrl = async (documentMeta: DocumentMeta) => {
    const {storage} = useStorageService()
    const orgId = getSelectedOrgId()
    const storageRef = ref(storage, encodeURI(`pracsys/${orgId}/${documentMeta.uuid}`))
    return await getDownloadURL(storageRef)
}

const storageGetFile = async (documentMeta: DocumentMeta) => {
    const {storage} = useStorageService()
    const path = await storageResolveUrl(documentMeta)
    const storageRef = ref(storage, path)
    const fileBytes = await getBytes(storageRef)
    const blob = new Blob([fileBytes], {type: documentMeta.contentType})

    return new File([blob], documentMeta.filename, {
        type: documentMeta.contentType,
        lastModified: Date.now()
    })
}

const storageDownloadFile = async (documentMeta: DocumentMeta) => {
    const file = await storageGetFile(documentMeta)
    const link = document.createElement('a')
    link.href = URL.createObjectURL(file)
    link.download = documentMeta.filename
    link.click()
}

const listIncomingDocumentsV2 = async (status = APPROVED, tenant?: Tenant): Promise<Array<IncomingDocument>> => {
    return await get<Array<IncomingDocument>>(INCOMING_PATH + V2_PATH, {status}, tenant?.organizationId) ?? []
}

const getIncomingDocumentsByStatusCount = async (status: string, tenant: Tenant) => await get(`${INCOMING_PATH}/count/${status}`, {}, tenant?.id)

const listDocumentsByTransactionUuid = async (transactionUuid: String): Promise<Array<DocumentMeta>> => {
    const path = `${DOCUMENTS}/transaction?transactionUuid=${transactionUuid}`
    return await get<DocumentMeta[]>(path) ?? []
}

const plugin = {
    install(app: VueApp) {
        app
            .provide(downloadFileKey, storageDownloadFile)
            .provide(uploadFilesKey, fileUploadBatch)
    }
}

const documentMetaDelete = async (documentMetaUuid: string) => {
    return await httpDelete(`${INCOMING_PATH}/${documentMetaUuid}`)
}

const documentMetaDeleteBatch = async (documentMetaUuids: string[]) => {
    await Promise.allSettled(documentMetaUuids.map(uuid => documentMetaDelete(uuid)))
}

const documentMetaUpdate = async (updatedDocumentMeta: DocumentMeta) => {
    return await patch(INCOMING_PATH, updatedDocumentMeta)
}

const setDocumentStatus = (cmd: DocumentStatusCommand) => {
    return post(`${INCOMING_PATH}/:setStatus`, cmd)
}

const approveIncomingDocument = async (uuid: string) => {
    return await setDocumentStatus({uuid, status: APPROVED})
}

export {
    plugin,
    resolveFileIcon,
    storageResolveUrl,
    storageGetFile,
    storageDownloadFile,
    documentMetaDelete,
    documentMetaDeleteBatch,
    documentMetaUpdate,
    approveIncomingDocument,
    listIncomingDocumentsV2,
    listDocumentsByTransactionUuid,
    getIncomingDocumentsByStatusCount,
    transactionManagerDocumentUrlsRevoke,
    transactionManagerDocumentUrls,

    // NEW STUFF
    fileUploadBatch,
    fileUpload,

    registerIncomingDocuments,

    INCOMING_DOCUMENT_DELETED,
    INCOMING_DOCUMENT_CREATED,
    INCOMING_DOCUMENT_EVENTS
}